import { useCallback, useEffect, useMemo, useState } from 'react';
import Toast from 'react-native-toast-message';
import { api } from '../../api/remembrApi';
import { useAppDispatch, useAppSelector } from '../../store/store';
import type { NotificationCardAction } from './components/NotificationCard';
import {
  dismiss as dismissNotif,
  loadNotifications,
  markRead,
} from './notificationsSlice';
import { parseShareToken } from './useNotificationDeepLink';
import { acknowledgeRecall } from '../inventory/custodySlice';
import {
  approveBorrowRequest,
  denyBorrowRequest,
} from '../borrowRequests/borrowRequestsSlice';
import {
  acceptGroupInvite,
  loadGroups,
  loadReceivedInvites,
  selectGroups,
  selectReceivedInvites,
  selectReceivedInvitesLoaded,
} from '../sharing/groupsSlice';
// Sprint 8 follow-up — read the raw shares cache so we can match
// regardless of status (PENDING / CLAIMED / RETURNED / DECLINED /
// CANCELLED / COMPLETED). The pre-filtered selectors are too narrow
// for this lookup — a share that auto-completes or gets cancelled
// would silently fall out of the cache and the notification card
// would lose its derived state.
import { selectShares } from '../sharing/sharingSlice';
import type {
  SharedByMe,
  SharedWithMe,
} from '../../api/sharingTypes';
import type { NotificationItem } from '../../api/types';

/**
 * Per-notification action handlers — Sprint 8 / FE-130b.
 *
 * Single hook centralizing every one-tap CTA the notification cards
 * expose (Claim / Decline / Accept / Approve / Deny / Recall etc.).
 *
 * Why one hook instead of per-action hooks: every action follows the
 * same 4-step pattern (optimistic mark-read → call domain endpoint →
 * reload list → toast). Inlining six near-identical hooks would be
 * noise; one map exposes everything the parent screens need with
 * stable references.
 *
 * Pending-state model: a single `pending` map keyed by notification
 * id stores the in-flight action name ('claim' / 'approve' / ...).
 * Cards use `pendingActionFor(id)` to render the right button as
 * loading. Cleared on success OR failure (toast surfaces the error).
 *
 * Notes on the "Decline" actions:
 *   - There's no backend "decline share" or "decline invite" endpoint
 *     today (filed as a follow-up). Both Decline buttons just dismiss
 *     the notification client-side — the underlying share/invite
 *     row stays PENDING server-side. That's intentional: the user
 *     can come back to it via the inbox, and the owner sees the row
 *     stay open. When BE ships explicit "decline" endpoints, we
 *     swap the body of these handlers without changing the API of
 *     this hook.
 */

type ActionName =
  | 'claim'
  | 'decline-share'
  | 'accept-invite'
  | 'decline-invite'
  | 'approve-borrow'
  | 'deny-borrow'
  | 'recall-ack'
  | 'remind-share'
  | 'recall-now';

/**
 * Sprint 8 follow-up — derived live state of a notification's
 * underlying domain entity.
 *
 * The notification row itself stays static once written, but the
 * *thing it points at* (a share, a borrow request, a custody event)
 * keeps changing. Without this derivation, a SHARE_RECEIVED card from
 * 4 hours ago still shows [Claim]/[Decline] even after the user
 * already claimed it — confusing.
 *
 * We resolve the live state by looking the entity up in the slice
 * caches that other surfaces already populate:
 *   - SHARE_RECEIVED  → `shared_with_me` cache by share_id
 *   - SHARE_CLAIMED   → `shared_by_me` cache by share_id  (sender side)
 *
 * Other types return `null` (no derivation available) and the card
 * renders with the default type-driven styling.
 *
 * `since` is the ISO timestamp of the state transition (when the
 * share was claimed/returned/declined) so the card can render
 * "Claimed · 2h ago" etc.
 */
export type NotificationDerivedState =
  | { kind: 'pending' }
  | { kind: 'claimed'; since: string | null }
  | { kind: 'returned'; since: string | null }
  | { kind: 'declined'; since: string | null }
  | { kind: 'cancelled'; since: string | null }
  | { kind: 'completed'; since: string | null }
  // FE-220 v2 — invite outcomes so notification cards show resolved
  // state instead of silently hiding the action buttons. Driven by
  // membership lookup in groups slice (joined = user is ACTIVE in the
  // group_id from payload; dismissed = neither pending nor a member).
  | { kind: 'joined'; since: string | null }
  | { kind: 'dismissed'; since: string | null }
  | null;

export interface UseNotificationActionsResult {
  /** True while ANY action is in-flight for the given notification. */
  isPendingFor: (notification: NotificationItem) => boolean;
  /** Specific in-flight action name for spinner placement, or undefined. */
  pendingActionFor: (notification: NotificationItem) => ActionName | undefined;

  // Action handlers — every one accepts the full notification so the
  // hook can look up target_id, payload.invite_token, etc. without
  // the parent screen having to pull them apart.
  claim: (n: NotificationItem) => Promise<void>;
  declineShare: (n: NotificationItem) => Promise<void>;
  acceptInvite: (n: NotificationItem) => Promise<void>;
  declineInvite: (n: NotificationItem) => Promise<void>;
  approveBorrow: (n: NotificationItem) => Promise<void>;
  denyBorrow: (n: NotificationItem) => Promise<void>;
  acknowledgeRecall: (n: NotificationItem) => Promise<void>;
  /** Sprint 8 follow-up — fires `POST /users/shares/{share_id}/send-reminder`
   *  so the owner can nudge a borrower whose share is CLAIMED. Wired
   *  to SHARE_CLAIMED notifications on the sender side. */
  remindShare: (n: NotificationItem) => Promise<void>;

  /** Live-state derivation for the underlying domain entity. See
   *  `NotificationDerivedState`. NotificationCard uses this to swap
   *  the icon disc color + add a status chip + fade the bg when
   *  the share has been actioned. */
  deriveStateFor: (n: NotificationItem) => NotificationDerivedState;

  /** Builds the per-notification action-button array (0..2 buttons)
   *  that the NotificationCard renders inline. Each surface calls
   *  this for every notification it renders; one source of truth
   *  for "what CTAs does this type get?". */
  buildActionsFor: (n: NotificationItem) => NotificationCardAction[];

  /** FE-227 v2 — Decline-with-notes sheet state. The hook tracks
   *  which borrow request is being declined (open vs closed),
   *  consumers mount `<DenyNotesSheet>` once at their root and
   *  bind it to these props. When `denySheet.visible` is true, the
   *  notification card's [Decline] button has been tapped; the
   *  sheet handles the actual API call + spinner + toast and
   *  closes itself on success. */
  denySheet: {
    visible: boolean;
    /** Synthetic `BorrowRequestDetails` shape built from the
     *  notification payload — has the requester name, item name,
     *  optional message, and request id needed by DenyNotesSheet's
     *  header + submit. */
    request: import('../../api/borrowTypes').BorrowRequestDetails | null;
    onClose: () => void;
  };
}

export function useNotificationActions(): UseNotificationActionsResult {
  const dispatch = useAppDispatch();
  const [pending, setPending] = useState<Record<number, ActionName>>({});
  // FE-227 v2 — decline-with-notes sheet state. When the user taps
  // [Decline] on a BORROW_REQUEST_RECEIVED card we don't fire the
  // thunk inline anymore — instead we open the DenyNotesSheet so
  // they can add an optional reason. The synthetic
  // BorrowRequestDetails carries just the fields the sheet renders
  // (id, requester_name, item_name, message, created_at).
  const [denyTarget, setDenyTarget] =
    useState<import('../../api/borrowTypes').BorrowRequestDetails | null>(null);

  // Live caches — read the raw `/users/shares` response (NOT the
  // pre-filtered PENDING/CLAIMED selectors). A V-mount battery share
  // can be in any of 6 statuses; if we only checked PENDING+CLAIMED
  // we'd miss RETURNED / DECLINED / CANCELLED rows and the card would
  // fall back to the "I don't know" state. Reading the full array
  // and letting `mapRowStatusToState` handle the routing is simpler
  // and complete.
  //
  // useMemo'd so the array identity is stable across re-renders that
  // don't actually change the data — keeps deriveStateFor's useCallback
  // dep array honest (otherwise every parent re-render busts the
  // memoization on every notification card downstream).
  const sharesCache = useAppSelector(selectShares);
  const sharedWithMe = useMemo<readonly SharedWithMe[]>(
    () => sharesCache?.shared_with_me ?? [],
    [sharesCache],
  );
  const sharedByMe = useMemo<readonly SharedByMe[]>(
    () => sharesCache?.shared_by_me ?? [],
    [sharesCache],
  );

  // FE-220 hotfix — pending group invites by token. Used to gate the
  // GROUP_INVITE_RECEIVED Accept/Decline buttons by **live entity state**
  // rather than `read_at`. The deep-link tap to AcceptInvite optimistically
  // marks the notif read; that shouldn't kill the buttons if the user
  // bails without finishing the flow.
  const pendingInvites = useAppSelector(selectReceivedInvites);
  const invitesLoaded = useAppSelector(selectReceivedInvitesLoaded);
  // Ensure invites are loaded on first mount of any surface that hosts
  // notification cards. Idempotent — thunk no-ops if already loaded.
  useEffect(() => {
    if (!invitesLoaded) dispatch(loadReceivedInvites());
  }, [dispatch, invitesLoaded]);
  const pendingInviteTokens = useMemo(() => {
    const s = new Set<string>();
    for (const inv of pendingInvites ?? []) {
      if (inv?.invite_token) s.add(inv.invite_token);
    }
    return s;
  }, [pendingInvites]);

  // FE-220 v2 — set of group_ids the viewer is currently a member of.
  // Used by deriveStateFor to render the "Joined" outcome chip on
  // invite notifications once the user has accepted (and the slice
  // has reloaded groups so the new membership is visible).
  const groups = useAppSelector(selectGroups);
  const memberGroupIds = useMemo(() => {
    const s = new Set<number>();
    for (const g of groups ?? []) {
      if (typeof g.id === 'number') s.add(g.id);
    }
    return s;
  }, [groups]);
  // FE-220 v3 — fallback lookup by group NAME for the common case
  // where the BE notification payload only carries `group_name`
  // (older rows / templates without `group_id`). Normalized + trimmed
  // so casing/whitespace differences don't cause false negatives.
  const memberGroupNames = useMemo(() => {
    const s = new Set<string>();
    for (const g of groups ?? []) {
      const n = (g as any).name;
      if (typeof n === 'string' && n.trim()) {
        s.add(n.trim().toLowerCase());
      }
    }
    return s;
  }, [groups]);

  /** Wraps a handler with pending-state + post-action mark-read +
   *  list reload. Catches errors, shows a toast, restores state. */
  const run = useCallback(
    async (
      n: NotificationItem,
      action: ActionName,
      body: () => Promise<void>,
      successToast: { text1: string; text2?: string },
    ) => {
      // Idempotency guard — short-circuit if the same action is
      // already in-flight for this notification. Without this, a fast
      // double-tap on Accept fires the API call twice; the second
      // call hits 410 invite_consumed and used to surface a scary
      // "Couldn't complete" toast. Now both the guard AND the soft-
      // success handling in groupsSlice handle the race cleanly, but
      // the guard saves the round-trip in the common case.
      let alreadyPending = false;
      setPending((p) => {
        if (p[n.id] === action) alreadyPending = true;
        return { ...p, [n.id]: action };
      });
      if (alreadyPending) return;
      try {
        await body();
        if (!n.read_at) dispatch(markRead(n.id));
        dispatch(loadNotifications({ mode: 'replace' }));
        Toast.show({ type: 'success', ...successToast });
      } catch (err: any) {
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          'Something went wrong';
        Toast.show({
          type: 'error',
          text1: "Couldn't complete",
          text2: detail,
        });
      } finally {
        setPending((p) => {
          const { [n.id]: _, ...rest } = p;
          return rest;
        });
      }
    },
    [dispatch],
  );

  const isPendingFor = useCallback(
    (n: NotificationItem) => pending[n.id] != null,
    [pending],
  );

  const pendingActionFor = useCallback(
    (n: NotificationItem): ActionName | undefined => pending[n.id],
    [pending],
  );

  /* ─────────────────────────────────────────────────────────────
   * Action handlers
   * ───────────────────────────────────────────────────────────── */

  /** Claim a SHARE_RECEIVED notification — calls the existing public
   *  claim endpoint. Token comes from action_url first (canonical
   *  BE-151 location), payload.share_url as fallback. */
  const claim = useCallback(
    (n: NotificationItem) => {
      const token =
        parseShareToken(n.action_url ?? undefined) ??
        parseShareToken(n.payload?.share_url as string | undefined);
      if (!token) {
        Toast.show({
          type: 'error',
          text1: "Can't claim",
          text2: 'Missing share link — open the email to claim.',
        });
        return Promise.resolve();
      }
      const itemName =
        (n.payload?.item_name as string | undefined) ?? 'the item';
      return run(
        n,
        'claim',
        async () => {
          await api.claimSharedItem(token, {});
        },
        { text1: 'Claimed', text2: `${itemName} is now in your inventory.` },
      );
    },
    [run],
  );

  /** Decline a SHARE_RECEIVED — no backend endpoint yet, so we
   *  dismiss the notification client-side. Share row stays PENDING
   *  server-side; the owner can revoke from their side. */
  const declineShare = useCallback(
    (n: NotificationItem) =>
      run(
        n,
        'decline-share',
        async () => {
          await dispatch(dismissNotif(n.id)).unwrap();
        },
        { text1: 'Dismissed', text2: 'The share is still pending if you change your mind.' },
      ),
    [dispatch, run],
  );

  /** Accept a CREW_INVITE_RECEIVED. After success, reload groups so
   *  the new crew shows up in CrewHub immediately (otherwise the user
   *  has to pull-to-refresh).
   *
   *  PR31 — graceful handling of the "already-redeemed" case. The
   *  notification can linger after the user accepted via the email
   *  link (BE-204's mark-read fan-out is async and can be missed by
   *  the FE's stale cache). When the server says 404 already-redeemed,
   *  treat it as a soft success: refresh groups + toast "You're
   *  already in {group}" instead of "Couldn't complete". The slice's
   *  rejected reducer separately marks the local notification read so
   *  the card disappears from the dashboard. */
  const acceptInvite = useCallback(
    (n: NotificationItem) => {
      const token = n.payload?.invite_token as string | undefined;
      if (!token) {
        Toast.show({
          type: 'error',
          text1: "Can't accept",
          text2: 'Invite token missing — try the email link.',
        });
        return Promise.resolve();
      }
      const groupName =
        (n.payload?.group_name as string | undefined) ?? 'the circle';
      return run(
        n,
        'accept-invite',
        async () => {
          try {
            await dispatch(acceptGroupInvite(token)).unwrap();
            // Happy path — refresh the crew list so the new
            // membership shows immediately.
            dispatch(loadGroups({ refresh: true }));
          } catch (err: any) {
            // Inspect the structured payload from groupsSlice's
            // rejectWithValue. ALREADY_REDEEMED = soft success.
            const isStructured = err && typeof err === 'object' && 'code' in err;
            if (isStructured && err.code === 'ALREADY_REDEEMED') {
              // Refresh groups in case we hadn't pulled the
              // membership down yet, then toast friendlily.
              dispatch(loadGroups({ refresh: true }));
              Toast.show({
                type: 'info',
                text1: `Already in ${groupName}`,
                text2: 'You joined this circle earlier.',
              });
              return; // suppress the run() helper's error toast
            }
            // Any other failure — surface to the run() helper, which
            // toasts the error using err.detail.
            throw err;
          }
        },
        { text1: 'Joined', text2: `Welcome to ${groupName}.` },
      );
    },
    [dispatch, run],
  );

  /** Decline a CREW_INVITE_RECEIVED.
   *
   *  TODAY: pure client-side dismiss. Server-side decline endpoint
   *  is filed as BE-212; until it lands the inviter has no way to
   *  know the recipient said no, the member row stays at INVITED
   *  forever, and the recipient can re-accept via the email link.
   *
   *  AFTER BE-212: this will fire `POST /groups/decline-invite` so
   *  the inviter's member-row + notification feed converge with the
   *  recipient's choice. */
  const declineInvite = useCallback(
    (n: NotificationItem) =>
      run(
        n,
        'decline-invite',
        async () => {
          await dispatch(dismissNotif(n.id)).unwrap();
        },
        {
          text1: 'Dismissed',
          text2: 'You can still accept later from your email link.',
        },
      ),
    [dispatch, run],
  );

  /** Approve a BORROW_REQUEST_RECEIVED — uses the existing borrow
   *  approval flow (same endpoint the BorrowRequestsManager screen
   *  fires). target_id on the notification is the borrow_request id. */
  const approveBorrow = useCallback(
    (n: NotificationItem) => {
      if (!n.target_id) return Promise.resolve();
      return run(
        n,
        'approve-borrow',
        async () => {
          await dispatch(
            approveBorrowRequest({ requestId: n.target_id! }) as any,
          ).unwrap();
        },
        { text1: 'Approved', text2: 'The requester has been notified.' },
      );
    },
    [dispatch, run],
  );

  /** Deny a BORROW_REQUEST_RECEIVED — symmetric to approve. */
  // FE-227 v2 — Decline now opens the DenyNotesSheet so the owner
  // can add an optional reason. The sheet handles the actual
  // denyBorrowRequest dispatch + toast + close — we just build the
  // synthetic BorrowRequestDetails from the notification's payload
  // and stash it so the sheet renders. Returns a no-op promise to
  // keep the signature compatible with the inline-thunk callers.
  const denyBorrow = useCallback(
    (n: NotificationItem) => {
      if (!n.target_id) return Promise.resolve();
      // Build a synthetic BorrowRequestDetails from the notification's
      // payload + meta. DenyNotesSheet only reads id / requester_name
      // / item_name / message / created_at, so we don't need the full
      // BE shape.
      const synthetic: import('../../api/borrowTypes').BorrowRequestDetails = {
        id: n.target_id,
        requester_name:
          (n.payload?.actor_name as string | undefined) ??
          n.actor_name ??
          'Someone',
        item_name:
          (n.payload?.item_name as string | undefined) ?? 'an item',
        item_description: '',
        item_image_url: null,
        message: (n.payload?.message as string | undefined) ?? null,
        expected_return_date:
          (n.payload?.expected_return_date as string | undefined) ?? null,
        sharing_type: 'BORROW',
        status: 'PENDING',
        created_at:
          (n.created_at as string | undefined) ?? new Date().toISOString(),
        expires_at:
          (n.payload?.expires_at as string | undefined) ?? '',
        days_until_expiry: 0,
        item_id:
          typeof n.payload?.item_id === 'number'
            ? (n.payload.item_id as number)
            : null,
        requester_user_id:
          typeof n.actor_user_id === 'number' ? n.actor_user_id : null,
        group_id:
          typeof n.payload?.group_id === 'number'
            ? (n.payload.group_id as number)
            : null,
        group_name:
          (n.payload?.group_name as string | undefined) ?? null,
        priority: 'NORMAL',
        queue_position: null,
      };
      setDenyTarget(synthetic);
      return Promise.resolve();
    },
    [],
  );

  /** Owner-side "Ask for return" — pings the borrower via the
   *  existing /users/shares/{id}/send-reminder endpoint. Wired to
   *  SHARE_CLAIMED notifications (the sender's "X claimed your
   *  Wooden shelf" surface) — gives the owner a one-tap nudge
   *  without leaving the inbox. */
  const remindShare = useCallback(
    (n: NotificationItem) => {
      const shareId = resolveShareId(n);
      if (shareId == null) {
        Toast.show({
          type: 'error',
          text1: "Can't send reminder",
          text2: 'Missing share id — open MyShares to manage manually.',
        });
        return Promise.resolve();
      }
      return run(
        n,
        'remind-share',
        async () => {
          await api.sendShareReminder(shareId);
        },
        { text1: 'Nudged', text2: 'The borrower just got an email.' },
      );
    },
    [run],
  );

  /** Acknowledge a CUSTODY_RECALL — owner's recall flow, fires
   *  the BE-147 endpoint via custodySlice. Same hook used to live
   *  in useRecallAckHandler; centralized here for consistency. */
  const acknowledgeRecallAction = useCallback(
    (n: NotificationItem) => {
      if (!n.target_id) return Promise.resolve();
      const itemName =
        (n.payload?.item_name as string | undefined) ?? 'the item';
      return run(
        n,
        'recall-ack',
        async () => {
          await dispatch(
            acknowledgeRecall({ itemId: n.target_id! }),
          ).unwrap();
        },
        { text1: 'Returned', text2: `${itemName} is back with the owner.` },
      );
    },
    [dispatch, run],
  );

  /** Derive the live state of a notification's underlying entity by
   *  cross-referencing the relevant /users/shares cache. Returns
   *  `null` for types we don't track (CUSTODY_*, BORROW_*, etc.) —
   *  the card falls back to default styling in that case.
   *
   *  Resolution order for the share id:
   *    1. n.target_id (canonical when target_type === 'share')
   *    2. n.payload.share_id (defensive fallback for older BE rows)
   *    3. item_name match in the relevant cache (last-ditch)
   *  When none of these resolve, we return `null` so the card falls
   *  back to the type-driven styling. */
  const deriveStateFor = useCallback(
    (n: NotificationItem): NotificationDerivedState => {
      // FE-220 v2 — invite outcomes. For GROUP_INVITE_RECEIVED (or its
      // legacy CREW_INVITE_RECEIVED alias), surface a resolved-state
      // chip once the user has acted on the invite:
      //
      //   - If the invite_token is STILL in receivedInvites cache →
      //     null (the action buttons render normally; not resolved yet)
      //   - Else if the user is now a member of the payload's group →
      //     "Joined" outcome chip
      //   - Else → "Dismissed" (declined OR cancelled by the inviter)
      //
      // The membership check uses payload.group_id to look up the
      // current user's groups list. Both the slice's groups list and
      // receivedInvites cache update as part of the accept thunk's
      // fulfilled branch (and the rejected ALREADY_REDEEMED branch),
      // so the outcome chip lands the moment the action completes.
      if (
        n.type === 'CREW_INVITE_RECEIVED' ||
        n.type === 'GROUP_INVITE_RECEIVED'
      ) {
        const token = n.payload?.invite_token as string | undefined;
        const groupId = n.payload?.group_id as number | undefined;
        const groupName = (n.payload?.group_name as string | undefined)
          ?.trim()
          .toLowerCase();
        // target_id is the canonical id when target_type === 'group'
        // (FE-220 v3 — covers payloads that lack a nested group_id).
        const targetGroupId =
          n.target_type === 'group' && typeof n.target_id === 'number'
            ? n.target_id
            : undefined;
        // Still pending — no outcome chip yet, action buttons render.
        // Defaults to "pending" when we haven't loaded invites yet so
        // we don't flash a wrong outcome on cold start.
        if (!invitesLoaded) return null;
        if (token && pendingInviteTokens.has(token)) return null;
        // Positive evidence the user joined: they're now in the group
        // referenced by the invite. Check group_id (payload OR target),
        // and fall back to group_name match against the user's groups
        // list (handles older BE rows that only set group_name).
        const idMatch =
          (groupId != null && memberGroupIds.has(groupId)) ||
          (targetGroupId != null && memberGroupIds.has(targetGroupId));
        const nameMatch =
          !!groupName && memberGroupNames.has(groupName);
        if (idMatch || nameMatch) {
          return { kind: 'joined', since: n.read_at };
        }
        // Conservative fallback: don't claim "Dismissed" without
        // positive evidence — the BE might just not have populated
        // group_id/name on the notification payload, OR the groups list
        // hasn't refreshed yet. Returning null hides both the buttons
        // (token is consumed) and the outcome chip; a follow-on
        // PR can add explicit outcome tracking via local state.
        // Specifically avoids the bug where a successful Accept that
        // landed before loadGroups({refresh:true}) finished was
        // briefly mislabeled "Dismissed".
        return null;
      }
      if (n.type !== 'SHARE_RECEIVED' && n.type !== 'SHARE_CLAIMED') {
        return null;
      }
      const shareId = resolveShareId(n);
      const itemName = (n.payload?.item_name as string | undefined)?.trim();

      const matchById = (
        rows: readonly (SharedByMe | SharedWithMe)[],
      ): SharedByMe | SharedWithMe | null => {
        if (shareId != null) {
          const hit = rows.find((s) => s.share_id === shareId);
          if (hit) return hit;
        }
        if (itemName) {
          const norm = itemName.toLowerCase();
          const hit = rows.find(
            (s) => (s.item_name ?? '').trim().toLowerCase() === norm,
          );
          if (hit) return hit;
        }
        return null;
      };

      if (n.type === 'SHARE_RECEIVED') {
        // Search the full shared_with_me array regardless of status.
        // Any state (PENDING / CLAIMED / RETURNED / DECLINED /
        // CANCELLED / COMPLETED) routes through mapRowStatusToState.
        const row = matchById(sharedWithMe);
        if (!row) return null;
        return mapRowStatusToState(row);
      }

      // SHARE_CLAIMED — sender side. Same logic against shared_by_me.
      const row = matchById(sharedByMe);
      if (!row) return null;
      return mapRowStatusToState(row);
    },
    [
      sharedWithMe,
      sharedByMe,
      invitesLoaded,
      pendingInviteTokens,
      memberGroupIds,
      memberGroupNames,
    ],
  );

  /** Per-type CTA layout. Single source of truth shared across
   *  NotificationsScreen, Dashboard, and CrewHubScreen so the buttons
   *  on a given notification look + behave identically everywhere.
   *
   *  Gating is by **live entity state**, not `read_at`. A SHARE_RECEIVED
   *  card stays actionable as long as the underlying share is PENDING,
   *  even if the user already scrolled past the notification once.
   *  Once the share moves to CLAIMED/DECLINED/RETURNED, the buttons
   *  collapse and a status chip takes over (rendered by NotificationCard
   *  via the `state` prop). */
  const buildActionsFor = useCallback(
    (n: NotificationItem): NotificationCardAction[] => {
      const pendingHere = pending[n.id];
      const derived = deriveStateFor(n);

      switch (n.type) {
        case 'SHARE_RECEIVED': {
          // Gate on the derived status of the underlying share row.
          // Hide buttons only when we have proof the share has moved
          // out of PENDING. When derivation is null (cache hasn't
          // loaded yet, or the share isn't in `shared_with_me` for
          // any reason), default to showing the buttons — the
          // recipient gets a Claim CTA that may 404 gracefully,
          // which is strictly better than hiding an actionable share
          // because the user once tapped its notification (which
          // would set read_at and silently kill the CTAs).
          if (derived && derived.kind !== 'pending') return [];
          return [
            {
              label: 'Claim',
              icon: 'check',
              variant: 'primary',
              onPress: () => claim(n),
              loading: pendingHere === 'claim',
              testID: `notification-action-claim-${n.id}`,
            },
            {
              label: 'Decline',
              variant: 'secondary',
              onPress: () => declineShare(n),
              loading: pendingHere === 'decline-share',
              testID: `notification-action-decline-share-${n.id}`,
            },
          ];
        }

        case 'SHARE_CLAIMED': {
          // Owner's "X claimed your share" notification — show
          // [Ask for return] when the share is still CLAIMED (i.e.
          // the borrower has it). Once returned/completed, no action.
          if (!derived || derived.kind !== 'claimed') return [];
          return [
            {
              label: 'Ask for return',
              icon: 'mail',
              variant: 'primary',
              onPress: () => remindShare(n),
              loading: pendingHere === 'remind-share',
              testID: `notification-action-remind-share-${n.id}`,
            },
          ];
        }

        // FE-226 — both legacy and renamed types take the invite action set.
        // FE-220 hotfix — gate on **live entity state**, not `read_at`.
        // Tapping the card to view AcceptInvite optimistically marks the
        // notif read (deep-link UX), but that shouldn't kill the buttons
        // if the user bails without accepting. The actual signal is:
        // does the invite token still exist in the pending receivedInvites
        // cache? When the slice removes it (after accept/decline succeeds
        // OR the inviter cancels), the buttons hide naturally.
        case 'CREW_INVITE_RECEIVED':
        case 'GROUP_INVITE_RECEIVED': {
          const token = n.payload?.invite_token as string | undefined;
          // Hide the buttons once we have positive evidence the invite
          // was resolved — either an outcome chip is being rendered
          // (joined / dismissed) OR receivedInvites has loaded and the
          // token is no longer pending.
          //
          // The previous `pendingInviteTokens.size === 0` fallback was
          // overzealous: after the user accepted their last invite the
          // set went empty and we incorrectly re-armed the buttons. On
          // refresh the receivedInvites cache reloads to 0 (token was
          // consumed), same bug. The right signal is `invitesLoaded`
          // AND token absent.
          if (derived && (derived.kind === 'joined' || derived.kind === 'dismissed')) {
            return [];
          }
          if (invitesLoaded && token && !pendingInviteTokens.has(token)) {
            return [];
          }
          const inviteStillPending =
            !token ||
            !invitesLoaded /* cold-start grace */ ||
            pendingInviteTokens.has(token);
          if (!inviteStillPending) return [];
          return [
            {
              label: 'Accept',
              icon: 'check',
              variant: 'primary',
              onPress: () => acceptInvite(n),
              loading: pendingHere === 'accept-invite',
              testID: `notification-action-accept-invite-${n.id}`,
            },
            {
              label: 'Decline',
              variant: 'secondary',
              onPress: () => declineInvite(n),
              loading: pendingHere === 'decline-invite',
              testID: `notification-action-decline-invite-${n.id}`,
            },
          ];
        }

        case 'BORROW_REQUEST_RECEIVED':
          if (n.read_at) return [];
          return [
            {
              label: 'Approve',
              icon: 'check',
              variant: 'primary',
              onPress: () => approveBorrow(n),
              loading: pendingHere === 'approve-borrow',
              testID: `notification-action-approve-borrow-${n.id}`,
            },
            {
              label: 'Decline',
              variant: 'secondary',
              onPress: () => denyBorrow(n),
              loading: pendingHere === 'deny-borrow',
              testID: `notification-action-deny-borrow-${n.id}`,
            },
          ];

        case 'CUSTODY_RECALL':
          if (n.read_at) return [];
          return [
            {
              label:
                pendingHere === 'recall-ack' ? 'Returning…' : 'Acknowledge & return',
              icon: 'undo',
              variant: 'destructive',
              onPress: () => acknowledgeRecallAction(n),
              loading: pendingHere === 'recall-ack',
              testID: `notification-recall-ack-${n.id}`,
            },
          ];

        default:
          return [];
      }
    },
    [
      pending,
      deriveStateFor,
      invitesLoaded,
      pendingInviteTokens,
      claim,
      declineShare,
      acceptInvite,
      declineInvite,
      approveBorrow,
      denyBorrow,
      acknowledgeRecallAction,
      remindShare,
    ],
  );

  // FE-227 v2 — expose decline-with-notes sheet props so consumers
  // can mount <DenyNotesSheet> bound to the hook's internal state.
  const denySheet = useMemo(
    () => ({
      visible: denyTarget != null,
      request: denyTarget,
      onClose: () => setDenyTarget(null),
    }),
    [denyTarget],
  );

  return useMemo<UseNotificationActionsResult>(
    () => ({
      isPendingFor,
      pendingActionFor,
      claim,
      declineShare,
      acceptInvite,
      declineInvite,
      approveBorrow,
      denyBorrow,
      acknowledgeRecall: acknowledgeRecallAction,
      remindShare,
      deriveStateFor,
      buildActionsFor,
      denySheet,
    }),
    [
      isPendingFor,
      pendingActionFor,
      claim,
      declineShare,
      acceptInvite,
      declineInvite,
      approveBorrow,
      denyBorrow,
      acknowledgeRecallAction,
      remindShare,
      deriveStateFor,
      buildActionsFor,
      denySheet,
    ],
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Module-level helpers (pure — no React state)
 * ────────────────────────────────────────────────────────────────────── */

/** Pull a share_id out of a notification's target_id / payload.
 *  Defensive — returns undefined when neither path resolves so the
 *  caller can degrade gracefully (e.g. show a "Missing share id"
 *  toast or fall back to the inbox). */
function resolveShareId(n: NotificationItem): number | undefined {
  if (n.target_type === 'share' && typeof n.target_id === 'number') {
    return n.target_id;
  }
  const fromPayload = n.payload?.share_id;
  if (typeof fromPayload === 'number') return fromPayload;
  return undefined;
}

/** Map a SharedByMe / SharedWithMe row's status to the derived-state
 *  shape consumed by NotificationCard. Centralized so both inbound
 *  and outbound paths in `deriveStateFor` apply the same mapping. */
function mapRowStatusToState(
  row: SharedByMe | SharedWithMe,
): NotificationDerivedState {
  switch (row.status) {
    case 'PENDING':
      return { kind: 'pending' };
    case 'CLAIMED':
      return { kind: 'claimed', since: row.claimed_at };
    case 'RETURNED':
      return { kind: 'returned', since: row.claimed_at };
    case 'DECLINED':
      return { kind: 'declined', since: row.claimed_at };
    case 'CANCELLED':
      return { kind: 'cancelled', since: row.claimed_at };
    case 'COMPLETED':
      return { kind: 'completed', since: row.claimed_at };
    default:
      return null;
  }
}
